/* Dawn cart contract retained: CartDrawer, CartDrawerItems and section IDs. */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    const options = { signal: this.lifecycle.signal };
    this.addEventListener('keyup', event => { if (event.code === 'Escape') this.close(); }, options);
    this.addEventListener('click', event => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    }, options);
    // Delegation also supports a header cart icon replaced by section rendering.
    document.addEventListener('click', event => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link) return;
      event.preventDefault();
      this.open(link);
    }, options);
    document.addEventListener('keydown', event => {
      if (event.code === 'Space' && event.target.closest('#cart-icon-bubble')) {
        event.preventDefault();
        this.open(event.target.closest('#cart-icon-bubble'));
      }
    }, options);
    this.setHeaderCartIconAccessibility();
    this.addEventListener('submit', event => {
      if (!event.target.matches('[data-oc-upsell]')) return;
      event.preventDefault();
      this.addRecommendation(event.target);
    }, options);
    document.addEventListener('shopify:section:select', event => {
      if (event.detail.sectionId === 'cart-drawer') this.open();
    }, options);
    document.addEventListener('shopify:block:select', event => {
      if (!this.contains(event.target)) return;
      this.open();
      event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, options);
    document.addEventListener('shopify:section:deselect', event => {
      if (event.detail.sectionId === 'cart-drawer') this.close();
    }, options);
    this.inert = true;
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    clearTimeout(this.focusTimer);
    if (this.classList.contains('active')) {
      removeTrapFocus();
      document.body.classList.remove('overflow-hidden');
    }
  }

  setHeaderCartIconAccessibility() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-expanded', String(this.classList.contains('active')));
    link.setAttribute('aria-controls', 'CartDrawer');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.inert = false;
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.setHeaderCartIconAccessibility();
    this.focusDrawer();
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  focusDrawer() {
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      if (!this.classList.contains('active')) return;
      const container = this.querySelector('.drawer__inner');
      if (container) trapFocus(container, container);
    }, 0);
  }

  close() {
    clearTimeout(this.focusTimer);
    this.classList.remove('active');
    const destination = this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble');
    removeTrapFocus(destination);
    this.inert = true;
    document.body.classList.remove('overflow-hidden');
    this.setHeaderCartIconAccessibility();
  }

  renderContents(state) {
    const parsed = this.getSectionDOM(state.sections?.['cart-drawer'], '#CartDrawer');
    if (!parsed) {
      // A successful add with a failed section render must not invite a duplicate add.
      window.location.assign(routes.cart_url);
      return;
    }
    this.replaceDrawer(parsed);
    const bubble = this.getSectionDOM(state.sections?.['cart-icon-bubble']);
    const currentBubble = document.getElementById('cart-icon-bubble');
    if (bubble && currentBubble) currentBubble.innerHTML = bubble.innerHTML;
    this.setHeaderCartIconAccessibility();
    this.open();
  }

  replaceDrawer(parsed) {
    const current = this.querySelector('#CartDrawer');
    const scroll = this.querySelector('.oc-cart__scroll')?.scrollTop || 0;
    current.innerHTML = parsed.innerHTML;
    current.setAttribute('style', parsed.getAttribute('style') || '');
    this.classList.toggle('is-empty', !!current.querySelector('cart-drawer-items.is-empty'));
    const scroller = this.querySelector('.oc-cart__scroll');
    if (scroller) scroller.scrollTop = scroll;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }
  getSectionDOM(html, selector = '.shopify-section') {
    return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }
  setActiveElement(element) {
    // An upsell button will be replaced; preserve the original external opener.
    if (element && !this.contains(element)) this.activeElement = element;
  }

  async addRecommendation(form) {
    if (this.busy) return;
    const error = form.querySelector('[data-oc-error]');
    if (this.querySelector('.cart__items--disabled')) {
      error.textContent = form.dataset.error;
      error.hidden = false;
      return;
    }
    this.busy = true;
    const button = form.querySelector('[type="submit"]');
    const checkout = this.querySelector('#CartDrawer-Checkout');
    const items = this.querySelector('#CartDrawer-CartItems');
    button.disabled = true;
    if (checkout) checkout.disabled = true;
    items?.classList.add('cart__items--disabled');
    error.hidden = true;
    try {
      const body = new FormData(form);
      body.set('sections', 'cart-drawer,cart-icon-bubble');
      body.set('sections_url', window.location.pathname);
      const response = await fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || form.dataset.error);
      this.renderContents(state);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    } catch (failure) {
      error.textContent = failure.message || form.dataset.error;
      error.hidden = false;
    } finally {
      this.busy = false;
      button.disabled = false;
      if (checkout) checkout.disabled = false;
      items?.classList.remove('cart__items--disabled');
    }
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    // Preserve Dawn's lifecycle/analytics; forward the event payload so we can
    // recognize HTML that the product form already owns and will render.
    super.connectedCallback();
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source === 'cart-items' || event.source === 'oc-reward-gift') return;
      return this.onCartUpdate(event);
    });
  }
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }
  async onCartUpdate(event) {
    const drawer = this.closest('cart-drawer');
    if (!drawer) return;
    // ProductForm renders these same bundled sections, including its delayed
    // quick-add-modal path. Other publishers still need the normal refresh.
    if (event?.source === 'product-form' &&
        drawer.getSectionDOM(event.cartData?.sections?.['cart-drawer'], '#CartDrawer')) return;
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) throw new Error('Cart refresh failed');
      const html = drawer.getSectionDOM(await response.text(), '#CartDrawer');
      if (!this.isConnected || !html) return;
      const focused = drawer.contains(document.activeElement);
      drawer.replaceDrawer(html);
      if (focused) drawer.focusDrawer();
    } catch {
      const error = drawer.querySelector('#CartDrawer-CartErrors');
      if (error) error.textContent = window.cartStrings.error;
    }
  }
}
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
